import { requestLaravel } from "../../../../../../utils/api";
import { ApiResponse, IProjectDocument } from "../../../../core/_models";
import { ILocationData } from "../../../core/_models";
import { getProjectFieldSection, saveProjectFieldSection, withProjectFieldValues } from "../../../core/project-fields/_request";
import { listSectionItems } from "../../../core/section-items/_request";
import { getProjectDocuments, updateProjectDocuments } from "../../../core/document/_request";
import { institutionalSponsorScope, stakeholderApi } from "../../ProjectInitiation-steps/core/_request";
import { IBenefitOutcome, IInvestmentNeedsPolicyIncentives, IStrategicFitSummaryDashboard, IJustification, IMarketSizeAndDemandAnalysis, IProjectScope, ISectorSpecificAnalysis, ISocioEconomicContext, ISWOTAnalysis, IPhase, GateAssessmentData, IKeyBusinessDrivers, ITechnicalOptionsAnalysis, IDocumentData, ITechnicalOption, IStrategicFuturesAndResilienceTesting, ISupplementaryInfrastructurePlanning, IStrategicSignificanceImpactFramework, STRATEGIC_SIGNIFICANCE_SECTION, SECTOR_SPECIFIC_ANALYSIS_SECTION, MARKET_SIZE_DEMAND_ANALYSIS_SECTION, STRATEGIC_FUTURES_SECTION } from "./_models";
import type { StepTab } from "../../ProjectInitiation-steps/core/_models";

/* =========================
   MINI EDITOR
========================= */

function updateprojectstage1minieditor<TResponse, TPayload extends { project_id: number }>(
    updateData: TPayload,
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<TResponse>>(
        "/project-stage-one/mini-editor/update",
        updateData,
        { signal }
    );
}

function getprojectstage1minieditor<TResponse, TPayload extends { project_id: number }>(
    payload: TPayload,
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<TResponse>>(
        "/project-stage-one/mini-editor/get",
        payload,
        { signal }
    );
}

/* =========================
   SECTOR ANALYSIS
========================= */

// Reads the parent section, which returns every tab's sub-section field in one call; tabs save through saveStepTab.
export function getSectorSpecificAnalysisData(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<ISectorSpecificAnalysis>(project_id, SECTOR_SPECIFIC_ANALYSIS_SECTION, signal);
}

/* =========================
   BENEFITS
========================= */

type OutcomeBenefitRow = {
    outcome_benefit_id: number;
    outcome_id: number;
    benefit_category_key: string;
    sub_categories: string[] | null;
};

const toBenefitOutcome = (row: OutcomeBenefitRow): IBenefitOutcome => ({
    outcome_id: Number(row.outcome_id),
    sub_categories: row.sub_categories ?? [],
});

// Every Stage 1 outcome assigned to a benefit category, grouped by the category's data_key.
export async function getOutcomeBenefits(project_id: number | string, signal?: AbortSignal): Promise<ApiResponse<Record<string, IBenefitOutcome[]>>> {
    const response = await requestLaravel<ApiResponse<OutcomeBenefitRow[]>>("/project/outcome-benefits/list", { project_id: Number(project_id), stage_id: STAGE_ONE_KEY }, { signal });
    const grouped: Record<string, IBenefitOutcome[]> = {};
    (response.data ?? []).forEach((row) => {
        (grouped[row.benefit_category_key] ??= []).push(toBenefitOutcome(row));
    });
    return { ...response, data: grouped };
}

// Replaces the outcomes assigned to one benefit category and returns them as saved.
export async function saveOutcomeBenefits(project_id: number | string, benefit_category_key: string, outcomes: IBenefitOutcome[]): Promise<ApiResponse<IBenefitOutcome[]>> {
    const response = await requestLaravel<ApiResponse<OutcomeBenefitRow[]>>("/project/outcome-benefits/save", {
        project_id: Number(project_id),
        stage_id: STAGE_ONE_KEY,
        benefit_category_key,
        outcomes,
    });
    return { ...response, data: (response.data ?? []).map(toBenefitOutcome) };
}

/* =========================
   INVESTMENT NEEDS & POLICY INCENTIVES
========================= */

// One project-fields section with no sub-sections; the accordions only group its fields.
export const INVESTMENT_NEEDS_SECTION = "investment_needs_policy_incentives";

export function getInvestmentNeedsPolicyIncentives(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<IInvestmentNeedsPolicyIncentives>(project_id, INVESTMENT_NEEDS_SECTION, signal);
}

export function updateInvestmentNeedsPolicyIncentives(project_id: number | string, values: IInvestmentNeedsPolicyIncentives, signal?: AbortSignal) {
    return writeStageSection<IInvestmentNeedsPolicyIncentives>(project_id, INVESTMENT_NEEDS_SECTION, values, signal);
}

/* =========================
   STRATEGIC FIT SUMMARY DASHBOARD
========================= */

const STRATEGIC_FIT_SUMMARY_SECTION = "strategic_fit_summary";

export function getStrategicFitSummaryDashboard(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<IStrategicFitSummaryDashboard>(project_id, STRATEGIC_FIT_SUMMARY_SECTION, signal);
}

export function updateStrategicFitSummaryDashboard(project_id: number | string, values: IStrategicFitSummaryDashboard, signal?: AbortSignal) {
    return writeStageSection<IStrategicFitSummaryDashboard>(project_id, STRATEGIC_FIT_SUMMARY_SECTION, values, signal);
}

/* =========================
   SOCIO ECONOMIC
========================= */

// One project-fields section with no sub-sections; the accordions only group its fields.
export const SOCIO_ECONOMIC_CONTEXT_SECTION = "socio_economic_context";

export function getSocioEconomicContext(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<ISocioEconomicContext>(project_id, SOCIO_ECONOMIC_CONTEXT_SECTION, signal);
}

export function updateSocioEconomicContext(project_id: number | string, values: ISocioEconomicContext, signal?: AbortSignal) {
    return writeStageSection<ISocioEconomicContext>(project_id, SOCIO_ECONOMIC_CONTEXT_SECTION, values, signal);
}

/* =========================
   MARKET ANALYSIS
========================= */

// Reads the parent section, which returns every tab's sub-section fields in one call; tabs save through saveStepTab.
export function getMarketSizeAndDemandAnalysis(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<IMarketSizeAndDemandAnalysis>(project_id, MARKET_SIZE_DEMAND_ANALYSIS_SECTION, signal);
}

/* =========================
   JUSTIFICATION
========================= */

// Needs Analysis and Demand Analysis fields of the justification project-fields section;
// Root Causes are Stage 1 problem tree causes, saved from their own popup.
export const JUSTIFICATION_SECTION = "justification";

export function getJustification(project_id: number, signal?: AbortSignal) {
    return readStageSection<Partial<IJustification>>(project_id, JUSTIFICATION_SECTION, signal);
}

/* =========================
   PROJECT SCOPE
========================= */

const INFRASTRUCTURE_PLANNING_STAGE_ID = 1;
// Stage key used by project section items and stage-wise APIs.
export const STAGE_ONE_KEY = "project_stage_1";
export const PROJECT_SCOPE_SECTION = "project_scope";
// Technical options have their own API, not a project-fields section; the step key names its documents.
export const TECHNICAL_OPTIONS_ANALYSIS_SECTION = "technical_options_analysis";
// Objectives, outcomes and SMART indicators have their own APIs too; the step key names its documents.
export const OBJECTIVES_SECTION = "objectives";
// Benefit outcomes have their own API as well; the step key names its documents.
export const BENEFITS_AND_DESIRED_OUTCOMES_SECTION = "benefits_and_desired_outcomes";

function readStageSection<T>(projectId: string | number, sectionKey: string, signal?: AbortSignal) {
    return getProjectFieldSection(projectId, INFRASTRUCTURE_PLANNING_STAGE_ID, sectionKey, signal)
        .then((response) => withProjectFieldValues<T>(response));
}

function writeStageSection<T>(projectId: string | number, sectionKey: string, payload: object, signal?: AbortSignal) {
    return saveProjectFieldSection(projectId, INFRASTRUCTURE_PLANNING_STAGE_ID, sectionKey, payload, signal)
        .then((response) => withProjectFieldValues<T>(response));
}

const SUPPLEMENTARY_INFRASTRUCTURE_SECTION = "supplementary_infrastructure_planning";

export function getSupplementaryInfrastructurePlanning(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<ISupplementaryInfrastructurePlanning>(project_id, SUPPLEMENTARY_INFRASTRUCTURE_SECTION, signal);
}

export function updateSupplementaryInfrastructurePlanning(project_id: number | string, values: ISupplementaryInfrastructurePlanning, signal?: AbortSignal) {
    return writeStageSection<ISupplementaryInfrastructurePlanning>(
        project_id,
        SUPPLEMENTARY_INFRASTRUCTURE_SECTION,
        { supplementary_infrastructure_requirements: values.supplementary_infrastructure_requirements },
        signal,
    );
}

// Reads the parent section, which returns every tab's sub-section fields in one call.
export function getStrategicSignificanceImpactFramework(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<IStrategicSignificanceImpactFramework>(project_id, STRATEGIC_SIGNIFICANCE_SECTION, signal);
}

// Saves one tab of a tab-wise step through its project-fields sub-section, sending only the
// tab's fields; the response holds only that tab's fields.
export function saveStepTab(project_id: number, tab: StepTab & { section: string }, values: object) {
    const payload: Record<string, unknown> = {};
    tab.fields.forEach((field) => {
        payload[field] = (values as Record<string, unknown>)[field];
    });
    return writeStageSection<Record<string, unknown>>(project_id, tab.section, payload);
}

// A project has one province: the form holds a single province_id (0 when none) and saves it as is.
// Values saved earlier as a one-item list are still read.
type ProjectScopeSectionValues = Omit<Partial<IProjectScope>, "provinces"> & { provinces?: number | string | string[] | null };

function toProvinceId(provinces: ProjectScopeSectionValues["provinces"]): number {
    const value = Array.isArray(provinces) ? provinces[0] : provinces;
    return Number(value) || 0;
}

function toProjectScopeValues(response: ApiResponse<ProjectScopeSectionValues>): ApiResponse<Partial<IProjectScope>> {
    if (response.status !== "ok" || !response.data) {
        return response as ApiResponse<Partial<IProjectScope>>;
    }
    const { provinces, ...values } = response.data;
    return { ...response, data: { ...values, provinces: toProvinceId(provinces) } };
}

export function getProjectScopeData(project_id: number, signal?: AbortSignal) {
    return readStageSection<ProjectScopeSectionValues>(project_id, PROJECT_SCOPE_SECTION, signal)
        .then(toProjectScopeValues);
}

export function getProjectScopeSponsors(project_id: number | string, signal?: AbortSignal) {
    return listSectionItems(institutionalSponsorScope(project_id, STAGE_ONE_KEY), signal);
}

// Signed agreements are Stage 1 project documents under these categories, not section fields.
export const SIGNED_AGREEMENT_CATEGORIES: readonly string[] = ["MoA", "MoU", "SLA"];
// The same categories by dev name, for the category list filter.
export const SIGNED_AGREEMENT_CATEGORY_DEV_NAMES: readonly string[] = ["moa", "mou", "sla"];

export function getProjectScopeSignedAgreements(project_id: number | string, signal?: AbortSignal): Promise<ApiResponse<IProjectDocument[]>> {
    return getProjectDocuments({ project_id: Number(project_id), stage_id: STAGE_ONE_KEY }, signal).then((response) =>
        response.status === "ok"
            // Supporting documents carry a section and are listed in their own accordion.
            ? { ...response, data: (response.data ?? []).filter((document) => !document.section && SIGNED_AGREEMENT_CATEGORIES.includes(document.category_name ?? "")) }
            : response
    );
}

export async function updateProjectScopeData(project_id: number, values: IProjectScope, signal?: AbortSignal): Promise<ApiResponse<Partial<IProjectScope>>> {
    // Sponsors are saved from their own popup and signed agreements as documents, so neither is part of the section.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { provinces, institutional_sponsors, signed_agreements, ...fields } = values;
    const response = await writeStageSection<ProjectScopeSectionValues>(
        project_id,
        PROJECT_SCOPE_SECTION,
        { ...fields, provinces: provinces || null },
        signal,
    ).then(toProjectScopeValues);

    const newAgreements = (signed_agreements ?? []).filter((document) => document.doc_id === 0);
    if (response.status !== "ok" || newAgreements.length === 0) {
        return response;
    }

    const upload = await updateProjectDocuments({ project_id, stage_id: STAGE_ONE_KEY, documents: newAgreements });
    if (upload.status !== "ok") {
        return { ...upload, data: response.data };
    }
    // Reload so the uploaded agreements carry their doc_id and are not uploaded again.
    const saved = await getProjectScopeSignedAgreements(project_id, signal);
    return saved.status === "ok"
        ? { ...response, data: { ...response.data, signed_agreements: saved.data } }
        : response;
}

/* =========================
   LOCATION
========================= */

export function getStgOneLocationData(project_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<{ locations: ILocationData[] }>>(
        "/project-stage-one/locations/list",
        { project_id },
        { signal }
    );
}

export interface insertUpdateOneLocationDataPayload extends ILocationData {
    project_id: number;
}

export function insertUpdateOneLocationData(
    payload: insertUpdateOneLocationDataPayload,
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<ILocationData>>(
        "/project-stage-one/locations/save",
        payload,
        { signal }
    );
}

export function deleteStgOneLocationData(
    loc_id: number,
    project_id: number,
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<[]>>(
        "/project-stage-one/locations/delete",
        { loc_id, project_id },
        { signal }
    );
}

/* =========================
   SWOT
========================= */

export const SWOT_ANALYSIS_SECTION = "swot_analysis";

export function getSWOTAnalysis(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<ISWOTAnalysis>(project_id, SWOT_ANALYSIS_SECTION, signal);
}

export function updateSWOTAnalysis(project_id: number | string, values: ISWOTAnalysis, signal?: AbortSignal) {
    return writeStageSection<ISWOTAnalysis>(project_id, SWOT_ANALYSIS_SECTION, values, signal);
}

/* =========================
   BUSINESS DRIVERS
========================= */

// key_business_drivers is the Stage 0 section; Stage 1 has its own.
export const KEY_BUSINESS_DRIVERS_SECTION = "key_business_drivers_why";

export function getKeyBusinessDriver(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<IKeyBusinessDrivers>(project_id, KEY_BUSINESS_DRIVERS_SECTION, signal);
}

// The section save turns business_case_drivers_remarks into each selected driver's free_text.
export function updateKeyBusinessDrivers(project_id: number | string, values: IKeyBusinessDrivers, signal?: AbortSignal) {
    return writeStageSection<IKeyBusinessDrivers>(project_id, KEY_BUSINESS_DRIVERS_SECTION, values, signal);
}

/* =========================
   PHASES
========================= */

export function getProjectPhases(project_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IPhase[]>>(
        "/project-stage-one/phase-deliverables/list",
        { project_id },
        { signal }
    );
}

export function insertUpdateProjectPhase(
    payload: IPhase & { project_id: number },
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<IPhase>>(
        "/project-stage-one/phase-deliverables/save",
        payload,
        { signal }
    );
}

export function deleteStgOnePhaseDeliverables(
    phase_id: number,
    project_id: number,
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<IPhase>>(
        "/project-stage-one/phase-deliverables/delete",
        { phase_id, project_id },
        { signal }
    );
}

/* =========================
   VALIDATION RULES
========================= */

export function getProjectValidationRulesData(
    project_id: number,
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<GateAssessmentData>>(
        "/project-stage-one/gate/validation-rules",
        { project_id },
        { signal }
    );
}

export function saveProjectStageOne(payload: { project_id: string | number; user_id?: string; dk_stage?: string }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<any>>(
        "/project-stage-one/stage/submit",
        { project_id: payload.project_id },
        { signal }
    );
}

/* =========================
   TECHNICAL OPTIONS
========================= */

export function getTechnicalOptionsAnalysis(project_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<ITechnicalOption[]>>(
        "/project-stage-one/technical-options/list",
        { project_id },
        { signal }
    );
}

export function CreateOrUpdateTechnicalOptionsAnalysis(
    updateData: ITechnicalOption & { project_id: number },
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<ITechnicalOption>>(
        "/project-stage-one/technical-options/save",
        updateData,
        { signal }
    );
}

export interface updateTechnicalOptionsAnalysisPayload extends Omit<
    ITechnicalOptionsAnalysis,
    "formAction" | "iscompleted"
> {
    project_id: number;
}

export function updateTechnicalPreferredOption(
    updateData: updateTechnicalOptionsAnalysisPayload,
    signal?: AbortSignal
) {
    return updateprojectstage1minieditor<ITechnicalOptionsAnalysis, updateTechnicalOptionsAnalysisPayload>(
        updateData,
        signal
    );
}

export function getTechnicalPreferredOption(
    payload: updateTechnicalOptionsAnalysisPayload,
    signal?: AbortSignal
) {
    return getprojectstage1minieditor<ITechnicalOptionsAnalysis, updateTechnicalOptionsAnalysisPayload>(
        payload,
        signal
    );
}


export function deleteTechnicalOption(
    project_id: number,
    row_id: number,
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<ITechnicalOption>>(
        "/project-stage-one/technical-options/delete",
        { project_id, row_id },
        { signal }
    );
}

type StrategicFuturesDocuments = Pick<IStrategicFuturesAndResilienceTesting, "documents">;

// Reads the parent section, which returns every tab's sub-section fields in one call; tabs save through saveStepTab.
export function getStrategicFuturesAndResilienceTesting(project_id: number | string, signal?: AbortSignal) {
    return readStageSection<IStrategicFuturesAndResilienceTesting>(project_id, STRATEGIC_FUTURES_SECTION, signal);
}

// Supporting documents are filed under the step's own document category, so they use the step's API.
export function getStrategicFuturesDocuments(project_id: number | string, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<StrategicFuturesDocuments>>(
        "/project-stage-one/strategic-futures-resilience-testing/get",
        { project_id: Number(project_id) },
        { signal }
    );
}

// Uploads the documents not saved yet; the response lists all of the step's documents. With none
// new (removed documents are deleted right away) there is nothing to send.
export async function uploadStrategicFuturesDocuments(project_id: number, values: StrategicFuturesDocuments, signal?: AbortSignal): Promise<ApiResponse<StrategicFuturesDocuments>> {
    const newDocuments = values.documents.filter((document) => document.doc_id === 0);
    if (newDocuments.length === 0) {
        return { status: "ok", message: "Strategic Futures and Resilience Testing saved successfully", data: { documents: values.documents } };
    }

    return requestLaravel<ApiResponse<StrategicFuturesDocuments>>(
        "/project-stage-one/strategic-futures-resilience-testing/save",
        { project_id, documents: newDocuments },
        { signal }
    );
}

/* =========================
   STAKEHOLDERS (Implementing Partners)
========================= */

export const stageOneStakeholderApi = stakeholderApi(STAGE_ONE_KEY);
